import React, { useCallback, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import ScanTooltipArrow from 'components/common/icons/timelineV2/mini-timeline/ScanTooltipArrow'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import GrinPopper from 'components/common/menu/GrinPopper'
import DazzedParagraph11 from 'components/common/text/DazzedParagraph11'
import moment from 'moment'
import ScanThumbnail from 'components/Patients/Timeline/V2/ScanThumbnail'
import { ButtonBase, Grid } from '@material-ui/core'
import { getObjectUrl } from 'utils/mediaUtils'
import TrackStatusTag from 'components/Patients/Timeline/V2/Tags/TrackStatusTag'
import UrgentScanTag from '../Tags/UrgentScanTag'
import AlignerNumber from '../AlignerNumber'
import { isEmpty, times } from 'lodash'
import OralStatus from './OralStatus'
import ScanNavChevron from 'components/common/icons/timelineV2/mini-timeline/ScanNavChevron'
import useFeatureFlags from 'hooks/useFeatureFlags'

const DENSE_MODE_HEIGHT_THRESHOLD = 10
/** Thumbnail widths the card is laid out from, and the wider of the two layouts a paged card holds. */
const DOUBLE_SCAN_THUMBNAIL_WIDTH = 155
const SINGLE_SCAN_THUMBNAIL_WIDTH = 115
const THUMBNAIL_GUTTER = 8
const TWO_THUMBNAIL_WIDTH = SINGLE_SCAN_THUMBNAIL_WIDTH * 2 + THUMBNAIL_GUTTER

const useStyles = makeStyles(theme => ({
  bodyContainer: {
    padding: '16px 20px',
    boxShadow: '0px 4px 14px 0px rgba(0, 0, 0, 0.1)',
    border: 'none'
  },
  // A scan with one thumbnail makes a narrower card than one with two. Paging between them moves the
  // right edge - and the next arrow sits on it - out from under the pointer, closing the tooltip
  // mid-click. Paged cards are held at the wider of the two.
  pagedContainer: {
    minWidth: TWO_THUMBNAIL_WIDTH
  },
  dateLabel: {
    color: 'var(--text-color-62)',
    marginTop: -8
  },
  thumbnailContainer: {
    width: ({ scansCount }) =>
      scansCount <= 1 ? `${DOUBLE_SCAN_THUMBNAIL_WIDTH}px` : `${SINGLE_SCAN_THUMBNAIL_WIDTH}px`
  },
  divider: {
    background: 'rgba(232, 232, 232, 1)',
    width: '100%',
    height: 1,
    margin: '6px 0'
  },
  navButton: {
    display: 'flex',
    '& .MuiButtonBase-root': {
      padding: 2,
      borderRadius: '100%'
    }
  },
  dots: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 6,
    marginTop: 10
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '100%',
    background: '#D5D8E0',
    padding: 0,
    border: 'none'
  },
  activeDot: {
    background: '#3C52EF'
  }
}))

const GrinScanTooltip = ({
  anchorEl,
  isOpen,
  date,
  scanNumber,
  alignerNumber,
  totalAlignerNumber,
  scans = [],
  scanType,
  trackStatus,
  urgency = {},
  oralScores = {},
  rawOralScores = {},
  onTrackReasonsClick = () => {},
  placement,
  // Paging, for a tooltip standing in for several scans taken close together. Left out everywhere
  // else, and then nothing below renders.
  stackPosition = 0,
  stackTotal = 0,
  onPrevious,
  onNext,
  onMouseEnter,
  onMouseLeave
}) => {
  const classes = useStyles({ scansCount: scans.length })
  const { t } = useTranslation()

  // Paging changes the card's height - a scan with an oral score is taller than one without - and a
  // card centred on its marker moves by half of that, out from under the pointer that is clicking
  // the arrows. Anchored by an edge instead, only the far side of it moves.
  const isPaged = stackTotal > 1
  const hasPrevious = isPaged && stackPosition > 0
  const hasNext = isPaged && stackPosition < stackTotal - 1
  const { oralHygieneScore: oralHygieneScoreFF } = useFeatureFlags()

  const [denseMode, setDenseMode] = useState(false)

  const offTrackReasons = useMemo(() => scans.map(scan => scan.offTrackReasons || []).flat(), [scans])
  const withOralScores = useMemo(() => oralHygieneScoreFF && !isEmpty(oralScores), [oralHygieneScoreFF, oralScores])

  const determineDenseMode = useCallback(
    popperBodyRef => {
      // Usually the last two scan items are too close to the bottom of the screen. As a result the scan tooltip overflows to the bottom of the screen.
      // This is what we do in order to solve this:
      // 1) Calculate the distance from the anchor element to the bottom of the screen.
      // 2) Calculate the popper element height.
      // 3) Check of the distance is less than half of the popper height (plus some threshold). If yes, fix the popper placement.

      const popperHeight = popperBodyRef.getBoundingClientRect().height
      const elementBottomCoord = anchorEl.getBoundingClientRect().bottom
      const distanceToBottom = window.innerHeight - elementBottomCoord

      if (distanceToBottom < popperHeight / 2 + DENSE_MODE_HEIGHT_THRESHOLD) {
        setDenseMode(true)
      } else {
        setDenseMode(false)
      }
    },
    [anchorEl]
  )

  const handlePopperEnter = useCallback(
    popperBodyRef => {
      if (anchorEl && popperBodyRef) {
        determineDenseMode(popperBodyRef)
      }
    },
    [anchorEl, determineDenseMode]
  )

  return (
    <GrinPopper
      customAnchorEl={anchorEl}
      open={isOpen}
      // A paged card is anchored by its top edge first: dense mode anchors the bottom, which means
      // a height change from paging moves the header - and the arrows in it - under the pointer.
      placement={placement ? placement : isPaged ? 'right-start' : denseMode ? 'right-end' : 'right'}
      arrowComponent={denseMode || placement || isPaged ? null : <ScanTooltipArrow />}
      arrowPlacement="left"
      className={classes.bodyContainer}
      onEnter={handlePopperEnter}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Grid
        container
        direction="column"
        spacing={1}
        justifyContent="center"
        className={isPaged ? classes.pagedContainer : ''}
      >
        <Grid item>
          {/* Only the paged header needs a row that keeps the arrows on it - the other views'
              header must lay out exactly as it did before this variation existed. */}
          <Grid container alignItems={isPaged ? 'center' : undefined} wrap={isPaged ? 'nowrap' : undefined}>
            {isPaged && (
              <Grid item className={classes.navButton} style={{ marginRight: 8 }}>
                <ButtonBase
                  disabled={!hasPrevious}
                  onClick={onPrevious}
                  aria-label={t('pages.patients.selectedPatient.timelineV2.miniTimeline.scanTooltip.previousScan')}
                >
                  <ScanNavChevron direction="left" disabled={!hasPrevious} />
                </ButtonBase>
              </Grid>
            )}
            <Grid item>
              <DazzedParagraph14 bold>
                {t(`pages.patients.selectedPatient.timelineV2.miniTimeline.scanTooltip.scanNumber`, { scanNumber })}
              </DazzedParagraph14>
            </Grid>
            {!!alignerNumber && (
              <Grid item style={{ marginLeft: 3 }}>
                <AlignerNumber alignerNumber={alignerNumber} totalAlignerNumber={totalAlignerNumber} />
              </Grid>
            )}
            <Grid item style={{ marginLeft: 9 }}>
              <UrgentScanTag urgencyStatus={urgency.urgencyStatus} reason={urgency.reason} denseMode />
            </Grid>
            {isPaged && (
              <Grid item className={classes.navButton} style={{ marginLeft: 'auto', paddingLeft: 12 }}>
                <ButtonBase
                  disabled={!hasNext}
                  onClick={onNext}
                  aria-label={t('pages.patients.selectedPatient.timelineV2.miniTimeline.scanTooltip.nextScan')}
                >
                  <ScanNavChevron direction="right" disabled={!hasNext} />
                </ButtonBase>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item>
          <DazzedParagraph11 thickness="thin" className={classes.dateLabel}>
            {moment(date).format('LLL')} ({moment(date).fromNow()})
          </DazzedParagraph11>
        </Grid>
        <Grid item>
          <Grid container direction="row" spacing={1} justifyContent="center">
            {scans.map(scan => (
              <Grid item key={`scan-tooltip-${scan.id}`}>
                <ScanThumbnail
                  mediaType="image"
                  src={getObjectUrl(scan.thumbnail)}
                  status={scan.scanSummaryStatus}
                  withAligner={scan.withAligner}
                  renderAlignerIconOverlay={scanType === 'doubleScan' || scan.withAligner}
                  containerClassName={classes.thumbnailContainer}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>

        <TrackStatusTag
          trackStatus={trackStatus}
          reasons={offTrackReasons}
          reasonsOnClick={onTrackReasonsClick}
          withLabel
        />

        {withOralScores && (
          <>
            <Grid item>
              <div className={classes.divider} />
            </Grid>
            <Grid item>
              <OralStatus oralScores={oralScores} rawOralScores={rawOralScores} />
            </Grid>
          </>
        )}

        {isPaged && (
          <Grid item>
            <div className={classes.dots}>
              {times(stackTotal, i => (
                <div
                  key={`scan-dot-${i}`}
                  className={[classes.dot, i === stackPosition ? classes.activeDot : ''].join(' ')}
                />
              ))}
            </div>
          </Grid>
        )}
      </Grid>
    </GrinPopper>
  )
}

export default React.memo(GrinScanTooltip)
